/**
 * useGymPermissions Hook
 *
 * Custom hook for checking user permissions at a specific gym.
 * Combines app-level roles, gym roles, and permission overrides.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import {
  type GymMembership,
  type GymRole,
  type GymPermission,
  type AppRole,
  hasPermission,
  getEffectivePermissions,
  normalizeGymRole,
  isStaffRole,
  GYM_PERMISSIONS,
} from '@flash/shared';

/**
 * State interface for gym permissions
 */
interface UseGymPermissionsState {
  /** User's membership at this gym */
  membership: GymMembership | null;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGymPermissions hook
 */
interface UseGymPermissionsResult extends UseGymPermissionsState {
  /** User's app-level role (super_admin, support, or null) */
  appRole: AppRole | null;
  /** User's role at this gym */
  gymRole: GymRole | null;
  /** Normalized gym role (climber -> member) */
  normalizedGymRole: Exclude<GymRole, 'climber'> | null;
  /** Whether user is a staff member at this gym */
  isStaff: boolean;
  /** Whether user is banned at this gym */
  isBanned: boolean;
  /** Whether user has a specific permission */
  hasPermission: (permission: GymPermission) => boolean;
  /** All effective permissions for this user at this gym */
  effectivePermissions: GymPermission[];
  /** Check if user can moderate content (comments, media) */
  canModerate: boolean;
  /** Check if user can ban other users */
  canBan: boolean;
  /** Check if user can manage staff */
  canManageStaff: boolean;
  /** Check if user can review flags */
  canReviewFlags: boolean;
  /** Check if user can view audit log */
  canViewAuditLog: boolean;
  /** Check if user can manage routes */
  canManageRoutes: boolean;
  /** Check if user is a super admin (app-level) */
  isSuperAdmin: boolean;
  /** Check if user is app support staff */
  isAppSupport: boolean;
}

/**
 * Hook to check user permissions at a specific gym
 *
 * @param gymId - The gym ID to check permissions for
 * @returns Object containing permission checks and membership info
 *
 * @example
 * ```tsx
 * const { canModerate, canBan, isStaff, loading } = useGymPermissions(gymId);
 *
 * if (loading) return <LoadingSpinner />;
 *
 * return (
 *   <div>
 *     {isStaff && <StaffBadge />}
 *     {canModerate && <ModerateButton />}
 *     {canBan && <BanButton />}
 *   </div>
 * );
 * ```
 */
export const useGymPermissions = (gymId: string | undefined): UseGymPermissionsResult => {
  const { user, userProfile } = useAuth();

  const [state, setState] = useState<UseGymPermissionsState>({
    membership: null,
    loading: true,
    error: null,
  });

  // Get app-level role from user profile
  const appRole = useMemo((): AppRole | null => {
    if (!userProfile) return null;
    // Check for appRole field
    const profile = userProfile as { appRole?: AppRole };
    return profile.appRole ?? null;
  }, [userProfile]);

  // Real-time subscription to gym membership
  useEffect(() => {
    if (!user || !gymId) {
      setState({ membership: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const membershipId = `${user.uid}_${gymId}`;
    const membershipRef = doc(db, 'gymMemberships', membershipId);

    const unsubscribe: Unsubscribe = onSnapshot(
      membershipRef,
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          setState({ membership: null, loading: false, error: null });
          return;
        }

        const data = docSnapshot.data();
        // Build membership object, only including optional fields if they exist
        const membership: GymMembership = {
          id: data['id'] as string,
          odKey: data['odKey'] as string,
          userId: data['userId'] as string,
          gymId: data['gymId'] as string,
          role: data['role'] as GymRole,
          isHomeGym: data['isHomeGym'] as boolean,
          createdAt: data['createdAt'],
          updatedAt: data['updatedAt'],
          // Conditionally spread optional fields
          ...(data['permissions'] ? { permissions: data['permissions'] as GymPermission[] } : {}),
          ...(data['revokedPermissions']
            ? { revokedPermissions: data['revokedPermissions'] as GymPermission[] }
            : {}),
          ...(data['isBanned'] !== undefined ? { isBanned: data['isBanned'] as boolean } : {}),
          ...(data['bannedAt'] ? { bannedAt: data['bannedAt'] } : {}),
          ...(data['bannedUntil'] !== undefined ? { bannedUntil: data['bannedUntil'] } : {}),
          ...(data['bannedBy'] ? { bannedBy: data['bannedBy'] as string } : {}),
          ...(data['bannedReason'] ? { bannedReason: data['bannedReason'] as string } : {}),
          ...(data['addedBy'] ? { addedBy: data['addedBy'] as string } : {}),
          ...(data['displayName'] ? { displayName: data['displayName'] as string } : {}),
          ...(data['email'] ? { email: data['email'] as string } : {}),
          ...(data['photoURL'] ? { photoURL: data['photoURL'] as string } : {}),
        };

        setState({ membership, loading: false, error: null });
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch membership';
        console.error('Error in gym membership subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, gymId]);

  // Derived values
  const gymRole = state.membership?.role ?? null;
  const normalizedGymRole = gymRole ? normalizeGymRole(gymRole) : null;
  const isStaff = gymRole ? isStaffRole(gymRole) : false;
  const isBanned = state.membership?.isBanned ?? false;

  // App-level role checks
  const isSuperAdmin = appRole === 'super_admin';
  const isAppSupport = appRole === 'super_admin' || appRole === 'support';

  // Permission checks
  const checkPermission = useCallback(
    (permission: GymPermission): boolean => {
      // Super admins have all permissions
      if (isSuperAdmin) return true;

      // App support has moderation-related permissions
      if (
        isAppSupport &&
        ['moderate_comments', 'moderate_media', 'review_flags', 'view_audit_log'].includes(
          permission
        )
      ) {
        return true;
      }

      // Check gym-level permissions
      if (!state.membership) return false;

      return hasPermission(state.membership, permission);
    },
    [state.membership, isSuperAdmin, isAppSupport]
  );

  // Get all effective permissions
  const effectivePermissions = useMemo((): GymPermission[] => {
    // Super admins have all permissions
    if (isSuperAdmin) return [...GYM_PERMISSIONS];

    // App support has specific permissions
    if (isAppSupport) {
      const supportPerms: GymPermission[] = [
        'moderate_comments',
        'moderate_media',
        'review_flags',
        'view_audit_log',
      ];
      if (!state.membership) return supportPerms;

      // Combine with gym-level permissions
      const gymPerms = getEffectivePermissions(state.membership);
      return Array.from(new Set([...supportPerms, ...gymPerms]));
    }

    // Regular gym-level permissions
    if (!state.membership) return [];
    return getEffectivePermissions(state.membership);
  }, [state.membership, isSuperAdmin, isAppSupport]);

  // Common permission checks
  const canModerate = checkPermission('moderate_comments');
  const canBan = checkPermission('ban_users');
  const canManageStaff = checkPermission('manage_staff');
  const canReviewFlags = checkPermission('review_flags');
  const canViewAuditLog = checkPermission('view_audit_log');
  const canManageRoutes = checkPermission('manage_routes');

  return {
    ...state,
    appRole,
    gymRole,
    normalizedGymRole,
    isStaff,
    isBanned,
    hasPermission: checkPermission,
    effectivePermissions,
    canModerate,
    canBan,
    canManageStaff,
    canReviewFlags,
    canViewAuditLog,
    canManageRoutes,
    isSuperAdmin,
    isAppSupport,
  };
};

export default useGymPermissions;
